/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { useEffect, useState } from "react";
import useFacebookPixel from "./hooks/useFacebookPixel";
import useGoogleAnalytics from "./hooks/useGoogleAnalytics";
import useGoogleTagManager from "./hooks/useGoogleTagManager";
import useSmartlook from "./hooks/useSmartlook";
import useSnapchatPixel from "./hooks/useSnapchatPixel";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useAtom } from "jotai";
import { analyticsHelpersIds } from "@/hooks/analytics-helpers-ids";
import useTiktokPixel from "./hooks/useTiktokPixel";
import useShouldFire from "./hooks/useShouldFire";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useGetProfileQuery } from "@/api/AppApi/services/auth";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";
import {
  checkoutResultAtom,
  checkoutResultDataLayerAtom,
} from "@/hooks/checkoutResultAtom";
import { getFirstAndLastName } from "./hooks/useEventTrackers/useEventTrackers";

const PageViewPixel = () => {
  const getCompany = useGetCompanyQuery();
  const shouldFire = useShouldFire();
  const [refresh, setRefresh] = useState(false);
  const [, setaAnalyticsHelpersIds] = useAtom(analyticsHelpersIds);
  const { theme } = getCompany?.data?.result! ?? {};
  const {
    smartlook_code,
    snapchat_code,
    facebook_code,
    google_analytics_id,
    google_tag,
    tiktok_code,
  } = theme?.settings! ?? {};
  const [checkoutResultDataLayer] = useAtom(checkoutResultDataLayerAtom);
  const [checkoutResult] = useAtom(checkoutResultAtom);
  const userDataLayer = !checkoutResultDataLayer?.isPurchaseEventTracked
    ? checkoutResultDataLayer?.userdata
    : undefined;
  const [isSignedIn] = useAtom(isSignedInAtom);
  const { data: userProfile } = useGetProfileQuery();

  const prepareCustomerDataForInit = () => {
    const customerData: any = {};

    const profile = userProfile?.result! ?? {};
    if (isSignedIn && userProfile?.result) {
      if (profile.name) {
        const { firstName, lastName } = getFirstAndLastName(profile.name);
        customerData.first_name = firstName;
        customerData.last_name = lastName;
      }
      if (profile.email) {
        customerData.email = profile.email;
        customerData.email_hash = toSHA256Hex(profile.email);
      }
      if (profile.mobile) {
        customerData.phone = profile.mobile;
        customerData.phone_hash = toSHA256Hex(profile.mobile);
      }
    } else if (userDataLayer && shouldFire) {
      if (userDataLayer.email) {
        customerData.email = userDataLayer.email;
        customerData.email_hash = toSHA256Hex(userDataLayer.email);
      }
      if (userDataLayer.phone) {
        customerData.phone = userDataLayer.phone;
        customerData.phone_hash = toSHA256Hex(userDataLayer.phone);
      }
      if (userDataLayer.name) {
        const { firstName, lastName } = getFirstAndLastName(userDataLayer.name);
        customerData.first_name = firstName;
        customerData.last_name = lastName;
      }
      console.log({ customerData });
    }
    if (checkoutResult?.cities_names) {
      customerData.city_name = checkoutResult?.cities_names.join(",");
    }

    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const fbclid = urlParams.get("fbclid");
      if (fbclid) {
        customerData.fbc = `fb.1.${Date.now()}.${fbclid}`;
      }
      const fbpCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith("_fbp="));
      if (fbpCookie) {
        customerData.fbp = fbpCookie.split("=")[1];
      }
      const agentIdCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith("agent_id="));
      if (agentIdCookie) {
        const agentId = agentIdCookie.split("=")[1];
        if (agentId) {
          customerData.external_id = agentId;
        }
      }
    }

    return customerData;
  };

  const advancedMatchingData = prepareCustomerDataForInit();
  useFacebookPixel(facebook_code, refresh, advancedMatchingData);
  useSnapchatPixel(snapchat_code, refresh);
  useTiktokPixel(tiktok_code, refresh);
  useSmartlook(smartlook_code, refresh);
  useGoogleAnalytics(google_analytics_id, refresh);
  useGoogleTagManager(google_tag, refresh);
  useEffect(() => {
    if (shouldFire) {
      setaAnalyticsHelpersIds({
        smartlook_code,
        snapchat_code,
        facebook_code,
        google_analytics_id,
        google_tag,
      });
    }
  }, [theme, shouldFire]);
  useEffect(() => {
    if (shouldFire && !refresh) {
      setRefresh(true);
    }
  }, [shouldFire]);
  if (!shouldFire) return;
  return <></>;
};

export default PageViewPixel;
